import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import Divider from '@mui/material/Divider';
import { useLanguage } from '../contexts/LanguageContext';

export default function PreviewShowcase() {
  const { translations } = useLanguage();
  const [imageLoaded, setImageLoaded] = React.useState(true);
  const imageSrc = `${process.env.PUBLIC_URL || ''}/assets/marketing/cupcake-store-preview.jpg`;

  const bullets = [
    translations.preview_section.bullet_one,
    translations.preview_section.bullet_two,
    translations.preview_section.bullet_three,
  ];

  return (
    <Container id="preview" sx={{ py: { xs: 8, sm: 14 } }}>
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.92fr) minmax(320px, 0.88fr)' },
        }}
      >
        <Stack spacing={2.5}>
          <Chip
            label={translations.preview_section.label}
            variant="outlined"
            sx={{
              alignSelf: 'flex-start',
              bgcolor: 'rgba(124,58,237,0.08)',
              borderColor: 'rgba(124,58,237,0.18)',
              color: 'primary.dark',
            }}
          />
          <Typography component="h2" variant="h3" sx={{ maxWidth: 620 }}>
            {translations.preview_section.title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, maxWidth: 620 }}>
            {translations.preview_section.description}
          </Typography>

          <Stack spacing={1.4} sx={{ pt: 1 }}>
            {bullets.map((bullet) => (
              <Stack key={bullet} direction="row" spacing={1.25} alignItems="center">
                <CheckCircleRoundedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {bullet}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Card
            variant="outlined"
            sx={{
              p: 3,
              mt: 1,
              borderRadius: 4,
              background: 'linear-gradient(180deg, rgba(124,58,237,0.06), rgba(255,255,255,0.98))',
            }}
          >
            <Stack spacing={2}>
              <FormatQuoteRoundedIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ lineHeight: 1.6 }}>
                {translations.preview_section.testimonial_quote}
              </Typography>
              <Box>
                <Typography variant="subtitle2">{translations.preview_section.testimonial_author}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {translations.preview_section.testimonial_role}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: 288,
              p: 1,
              borderRadius: '30px',
              background: 'linear-gradient(180deg, #221D33, #120E1B)',
              boxShadow: '0 34px 90px rgba(36, 18, 71, 0.24)',
              animation: 'previewFloat 6s ease-in-out infinite',
              '@keyframes previewFloat': {
                '0%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-10px)' },
                '100%': { transform: 'translateY(0px)' },
              },
            }}
          >
            <Box
              sx={{
                overflow: 'hidden',
                borderRadius: '23px',
                aspectRatio: '720 / 1497',
                bgcolor: '#F8F6FF',
                position: 'relative',
              }}
            >
              {imageLoaded ? (
                <Box
                  component="img"
                  src={imageSrc}
                  alt="Rewardiou mobile preview"
                  onError={() => setImageLoaded(false)}
                  loading="lazy"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                />
              ) : (
                <Stack spacing={2.5} sx={{ p: 3 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      bgcolor: 'rgba(124,58,237,0.1)',
                      border: '1px solid rgba(124,58,237,0.12)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 1 }}>
                      {translations.preview_section.fallback_title}
                    </Typography>
                    <Typography variant="h3" sx={{ lineHeight: 1 }}>
                      {translations.preview_section.fallback_metric_value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      {translations.preview_section.fallback_metric_label}
                    </Typography>
                  </Box>

                  <Card variant="outlined" sx={{ borderRadius: 4, p: 2.5 }}>
                    <Stack spacing={1}>
                      <Typography variant="h6">{translations.preview_section.fallback_card_title}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                        {translations.preview_section.fallback_card_description}
                      </Typography>
                    </Stack>
                  </Card>

                  <Divider />

                  <Stack spacing={1.4}>
                    {bullets.map((bullet) => (
                      <Box
                        key={bullet}
                        sx={{
                          px: 1.5,
                          py: 1.2,
                          borderRadius: 3,
                          bgcolor: 'common.white',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {bullet}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
